import React, { useState, useEffect, useRef, useMemo } from "react";
import JoditEditor from "jodit-react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { getWithTokenAPI, putWithTokenAPI } from "../../util/Api";
import "./CMSManagement.css";

const TABS = [
  { id: "terms-and-conditions", label: "Terms & Conditions" },
  { id: "privacy-policy", label: "Privacy Policy" },
  { id: "cancellation-refund-policy", label: "Cancellation & Refund" },
  { id: "shipping-exchange-policy", label: "Shipping & Exchange" },
  { id: "contact-us", label: "Contact Us" },
];

const TYPES = [
  { id: "student", label: "Student" },
  { id: "institutions", label: "Institutions" },
];

const CMSManagement = () => {
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [activeType, setActiveType] = useState(TYPES[0].id);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const { token } = useSelector((state) => state.auth);

  const editor = useRef(null);
  const config = useMemo(
    () => ({
      readonly: false,
      placeholder: "Start typing...",
      height: 400,
    }),
    []
  );

  // Form states
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  
  // Contact Us states
  const [contactData, setContactData] = useState({
    heading: "",
    email: "",
    phone: "",
    whatsapp: "",
    address: "",
    mapLink: "",
    workingHours: "",
  });

  useEffect(() => {
    fetchData();
  }, [activeTab, activeType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Use query param for all APIs as per instructions
      const url = `/${activeTab}?type=${activeType}`;
      const res = await getWithTokenAPI(token, url);

      if (res?.statusCode === 200 && res.data && res.data.length > 0) {
        // Fallback to first if type doesn't strictly match in API response
        const item = res.data.find((d) => d.type === activeType) || res.data[0];
        setData(item);
        setTitle(item.title || item.heading || "");
        setContent(item.content || "");
        
        if (activeTab === "contact-us") {
          setContactData({
            heading: item.heading || "",
            email: item.email || "",
            phone: item.phone || "",
            whatsapp: item.whatsapp || "",
            address: item.address || "",
            mapLink: item.mapLink || "",
            workingHours: item.workingHours || "",
          });
        }
      } else {
        setData(null);
        setTitle("");
        setContent("");
        setContactData({
          heading: "",
          email: "",
          phone: "",
          whatsapp: "",
          address: "",
          mapLink: "",
          workingHours: "",
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to fetch data", "error");
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!data?._id) {
      Swal.fire("Error", "No record found to update. Missing data ID.", "error");
      return;
    }
    setLoading(true);
    try {
      let payload = { content };
      
      if (activeTab !== "contact-us") {
        payload.title = title;
      } else {
        payload = {
          ...payload,
          heading: title || contactData.heading,
          email: contactData.email,
          phone: contactData.phone,
          whatsapp: contactData.whatsapp,
          address: contactData.address,
          mapLink: contactData.mapLink,
          workingHours: contactData.workingHours,
        };
      }

      const res = await putWithTokenAPI(token, `/${activeTab}/${data._id}`, payload);
      if (res?.statusCode === 200) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Updated successfully",
          showConfirmButton: false,
          timer: 1500,
        });
        fetchData();
      } else {
        Swal.fire("Error", res?.message || "Update failed", "error");
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to update", "error");
    }
    setLoading(false);
  };

  return (
    <div className="cms-container">
      <div className="cms-header">
        <h2 className="cms-title">CMS Management</h2>
        
        {/* Type Toggle */}
        <div className="type-toggle">
          {TYPES.map((type) => (
            <button
              key={type.id}
              className={`type-btn ${activeType === type.id ? "active" : ""}`}
              onClick={() => setActiveType(type.id)}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      <div className="cms-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="cms-content-area">
        {loading ? (
          <div className="cms-loading">Loading...</div>
        ) : (
          <div className="cms-form">
            <div className="form-group">
              <label>Title / Heading</label>
              <input
                type="text"
                className="cms-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter title"
              />
            </div>

            {activeTab === "contact-us" && (
              <div className="contact-grid">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    className="cms-input"
                    value={contactData.email}
                    onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    className="cms-input"
                    value={contactData.phone}
                    onChange={(e) => setContactData({ ...contactData, phone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>WhatsApp</label>
                  <input
                    type="text"
                    className="cms-input"
                    value={contactData.whatsapp}
                    onChange={(e) => setContactData({ ...contactData, whatsapp: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Working Hours</label>
                  <input
                    type="text"
                    className="cms-input"
                    value={contactData.workingHours}
                    onChange={(e) => setContactData({ ...contactData, workingHours: e.target.value })}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Address</label>
                  <input
                    type="text"
                    className="cms-input"
                    value={contactData.address}
                    onChange={(e) => setContactData({ ...contactData, address: e.target.value })}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Map Link</label>
                  <input
                    type="text"
                    className="cms-input"
                    value={contactData.mapLink}
                    onChange={(e) => setContactData({ ...contactData, mapLink: e.target.value })}
                  />
                </div>
              </div>
            )}

            <div className="form-group editor-group">
              <label>Content</label>
              <JoditEditor
                ref={editor}
                value={content}
                config={config}
                tabIndex={1}
                onBlur={(newContent) => setContent(newContent)}
                onChange={(newContent) => setContent(newContent)}
                className="cms-editor"
              />
            </div>

            <div className="cms-actions">
              <button className="save-btn" onClick={handleSave} disabled={loading || !data}>
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CMSManagement;
